/**
 * FlahaSOIL v2 — login page (Phase 9A-G).
 *
 * Posts credentials to `/api/v2/auth/login` via `useAuth().actions.login`.
 * On success the HttpOnly refresh cookie is set by the backend, the
 * access token is stored in memory by `AuthProvider`, and we navigate
 * to the `?next=` URL (default `/dashboard`).
 *
 * Server errors arrive as `ApiClientError`; the standard error envelope
 * has `code` and `message` fields — we surface `message` directly.
 */

import {
	Alert,
	Box,
	Button,
	Divider,
	Link,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { useState, type FormEvent } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../auth";

interface FieldErrors {
	email?: string;
	password?: string;
}

function readNext(search: string): string {
	const params = new URLSearchParams(search);
	const next = params.get("next");
	if (!next || !next.startsWith("/") || next.startsWith("//")) return "/dashboard";
	return next;
}

export function LoginPage() {
	const { actions } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [errors, setErrors] = useState<FieldErrors>({});
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	function validate(): boolean {
		const next: FieldErrors = {};
		if (!email.trim()) next.email = "Email is required.";
		if (!password) next.password = "Password is required.";
		setErrors(next);
		return Object.keys(next).length === 0;
	}

	async function onSubmit(e: FormEvent<HTMLFormElement>) {
		e.preventDefault();
		setSubmitError(null);
		if (!validate()) return;
		setSubmitting(true);
		try {
			await actions.login({ email: email.trim(), password });
			navigate(readNext(location.search), { replace: true });
		} catch (err) {
			const msg = err instanceof Error ? err.message : "Login failed.";
			setSubmitError(msg);
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Box data-testid="login-surface">
			<Stack spacing={1} sx={{ mb: 3 }}>
				<Typography
					variant="overline"
					sx={{ color: "primary.main", letterSpacing: "0.12em", fontWeight: 700 }}
				>
					Soil intelligence workspace
				</Typography>
				<Typography component="h1" variant="h4">
					Sign in to FlahaSOIL
				</Typography>
				<Typography variant="body2" sx={{ color: "text.secondary" }}>
					Continue to your authenticated organization, projects, analyses, and reports.
				</Typography>
			</Stack>
			<form noValidate onSubmit={onSubmit} aria-busy={submitting}>
				<Stack spacing={2.25}>
					{submitError && (
						<Alert severity="error" role="alert">
							{submitError}
						</Alert>
					)}
					<TextField
						label="Email"
						type="email"
						autoComplete="email"
						required
						fullWidth
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						error={Boolean(errors.email)}
						helperText={errors.email}
					/>
					<TextField
						label="Password"
						type="password"
						autoComplete="current-password"
						required
						fullWidth
						value={password}
						onChange={(e) => setPassword(e.target.value)}
						error={Boolean(errors.password)}
						helperText={errors.password}
					/>
					<Button
						type="submit"
						variant="contained"
						size="large"
						fullWidth
						disabled={submitting}
					>
						{submitting ? "Signing in…" : "Sign in"}
					</Button>
					<Typography variant="body2" sx={{ textAlign: "center" }}>
						<Link component={RouterLink} to="/forgot-password" fontWeight={600}>
							Forgot password?
						</Link>
					</Typography>
					<Divider>New to FlahaSOIL?</Divider>
					<Typography variant="body2" sx={{ textAlign: "center", color: "text.secondary" }}>
						<Link component={RouterLink} to="/register" fontWeight={700}>
							Create an account
						</Link>{" "}
						to establish your personal organization workspace.
					</Typography>
				</Stack>
			</form>
		</Box>
	);
}
